// Enchantress Avatar. "Whenever you cast a spell, you may animate
// target aura until your next turn. It's an aura minion with power
// equal to its cost."
//
// Pure helpers for the animate-aura transform. The orchestration
// (target picker, delayed revert) lives in GameBoard; this module
// only owns the predicates + the snapshot/transform/revert recipe
// that mirrors Animist's Magic→Spirit shape (`./animistSpirit.js`).
//
// Identity revert at the caster's next turn restores every mutated
// field from the snapshot. Type, subTypes (Aura stays put; Minion is
// removed), base/current stats, the `_enchantressAnimated` marker.
// The revert pipeline is reused for any future "until your next turn"
// identity transform.

const AURA_SUBTYPE = 'aura';

function readSubTypes(card) {
  const raw = card?.subTypes;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') return raw.split(/[,;\s]+/).filter(Boolean);
  return [];
}

/**
 * True iff the card is currently animatable by Enchantress. Must be
 * in play and carry the 'Aura' subtype on its CURRENT shape (a card
 * that's already been animated has type='Minion' but still has Aura
 * in its subTypes; we treat re-animation as a no-op via the
 * `_enchantressAnimated` marker check at the GameBoard call site).
 */
export function isAnimatableAura(card) {
  if (!card) return false;
  // Real catalog auras carry their aura-ness in the top-level `type`
  // ('Aura'), with empty subTypes. An already-animated card keeps the
  // Aura subtype it was stamped with, so the subtype check still
  // matches the in-play minion form.
  if (String(card.type).toLowerCase() === AURA_SUBTYPE) return true;
  const subs = readSubTypes(card).map((s) => String(s).toLowerCase());
  return subs.includes(AURA_SUBTYPE);
}

/**
 * Walk the meshes Map and return every cardInstance currently
 * animatable. Aura cards from EITHER controller are eligible. The
 * card text says "target aura" with no allied/enemy qualifier.
 */
export function findAnimatableAuras(meshes) {
  const out = [];
  if (!meshes) return out;
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (card._enchantressAnimated) continue;
    if (!isAnimatableAura(card)) continue;
    out.push(card);
  }
  return out;
}

/**
 * Capture every field the transform mutates so the revert can restore
 * the original identity verbatim. Stored on the card itself so the
 * revert queue only needs to track `cardId + revertPlayer + turnNumber`.
 */
export function snapshotAuraIdentity(card) {
  return {
    type: card.type,
    subTypes: Array.isArray(card.subTypes) ? [...card.subTypes] : card.subTypes,
    baseAttack: card.baseAttack,
    baseLife: card.baseLife,
    currentAttack: card.currentAttack,
    currentLife: card.currentLife,
  };
}

/**
 * Mutate the picked aura into a Minion whose attack and defence equal
 * its printed cost. Returns true when the transform applied, false
 * when the card was already animated (idempotent).
 *
 * Mirrors Animist's shape (`applyCastAsSpiritTransform`) but the
 * boundary is different: Animist transforms at the hand→table moment,
 * Enchantress transforms an existing in-play card.
 */
export function applyAnimateAuraTransform(cardInstance, fullCard) {
  if (!cardInstance || cardInstance._enchantressAnimated) return false;
  const rawCost = fullCard?.cost ?? cardInstance.cost;
  const parsed = parseInt(rawCost, 10);
  const power = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;

  cardInstance._enchantressOriginal = snapshotAuraIdentity(cardInstance);
  cardInstance.type = 'Minion';
  // Aura subtype stays put. The new shape is an "aura minion", not a
  // straight minion. Existing non-Aura subtypes are preserved so
  // future "target Spirit" / "target Demon" filters can still see them.
  cardInstance.baseAttack = power;
  cardInstance.baseLife = power;
  cardInstance.currentAttack = power;
  cardInstance.currentLife = power;
  cardInstance._enchantressAnimated = true;
  return true;
}

/**
 * Restore the original identity captured by `applyAnimateAuraTransform`.
 * Safe to call on a card that was never animated (returns false).
 */
export function revertAnimateAuraTransform(cardInstance) {
  if (!cardInstance || !cardInstance._enchantressAnimated) return false;
  const snap = cardInstance._enchantressOriginal;
  if (!snap) return false;
  cardInstance.type = snap.type;
  cardInstance.subTypes = Array.isArray(snap.subTypes) ? [...snap.subTypes] : snap.subTypes;
  cardInstance.baseAttack = snap.baseAttack;
  cardInstance.baseLife = snap.baseLife;
  cardInstance.currentAttack = snap.currentAttack;
  cardInstance.currentLife = snap.currentLife;
  cardInstance._enchantressAnimated = false;
  cardInstance._enchantressOriginal = null;
  return true;
}

/**
 * Describe an animated aura's full new shape for the network. The
 * original per-stat broadcast only carried currentAttack/currentLife,
 * so the opponent kept the printed Aura (type 'Aura', null stats) and
 * rendered a 0-power phantom. This payload carries everything the
 * remote copy needs. Type, base stats, and the revert snapshot. So
 * both sides agree the card is now a Minion.
 */
export function animateSyncPayload(card) {
  return {
    cardId: card.id,
    type: card.type,
    subTypes: Array.isArray(card.subTypes) ? [...card.subTypes] : card.subTypes,
    baseAttack: card.baseAttack,
    baseLife: card.baseLife,
    currentAttack: card.currentAttack,
    currentLife: card.currentLife,
    original: card._enchantressOriginal,
  };
}

/**
 * Adopt a received `animateSyncPayload` onto the local card copy.
 * Mirrors `applyAnimateAuraTransform`'s field set so the remote shape
 * is identical, and stashes the snapshot so the receiver can later
 * revert independently (timed revert / leave-play reset).
 */
export function applyAnimateSync(card, data) {
  if (!card || !data) return;
  card.type = data.type;
  if (data.subTypes !== undefined) {
    card.subTypes = Array.isArray(data.subTypes) ? [...data.subTypes] : data.subTypes;
  }
  card.baseAttack = data.baseAttack;
  card.baseLife = data.baseLife;
  card.currentAttack = data.currentAttack;
  card.currentLife = data.currentLife;
  card._enchantressAnimated = true;
  card._enchantressOriginal = data.original || null;
}
